import type { FileIntakeAccept } from '@axiom/ui';
import {
  AttachmentBadgeUtil,
  AttachmentKind,
  FileExtension,
  FileMimeType,
} from '@axiom/ui';

import type { ChatAttachment } from '../types/chat.types';

/**
 * ChatAttachmentAcceptUtil - I decide what the composer takes, and say so in the picker, in a
 * sentence and against a drop. ONLY concerned with that rule - no classification, no previews,
 * no transfer.
 */
export const ChatAttachmentAcceptUtil = {
  kindLabels: [
    [AttachmentKind.PDF, 'PDF'],
    [AttachmentKind.DOCUMENT, 'Word'],
    [AttachmentKind.TEXT, 'text'],
    [AttachmentKind.IMAGE, 'images'],
  ] satisfies Array<[AttachmentKind, string]>,

  accepts: [
    FileExtension.PDF,
    FileExtension.DOC,
    FileExtension.DOCX,
    FileExtension.RTF,
    FileExtension.TXT,
    FileExtension.MD,
    FileExtension.CSV,
    FileExtension.JSON,
    FileExtension.PNG,
    FileExtension.JPG,
    FileExtension.JPEG,
    FileExtension.WEBP,
    FileExtension.GIF,
    FileExtension.HEIC,
    FileExtension.HEIF,
    FileExtension.TIF,
    FileExtension.TIFF,
    FileMimeType.PDF,
    FileMimeType.DOC,
    FileMimeType.DOCX,
    FileMimeType.TEXT,
    FileMimeType.JSON,
    FileMimeType.CSV,
    FileMimeType.IMAGE,
  ] satisfies FileIntakeAccept[],

  acceptSummary() {
    const present = new Set(
      ChatAttachmentAcceptUtil.accepts
        .filter(accepted => accepted.startsWith('.'))
        .map(accepted => AttachmentBadgeUtil.kindOf({ fileName: accepted }))
    );
    const labelled = ChatAttachmentAcceptUtil.kindLabels.filter(([kind]) =>
      present.has(kind)
    );
    const unlabelled = [...present].filter(
      kind =>
        !ChatAttachmentAcceptUtil.kindLabels.some(([known]) => known === kind)
    );

    return [...labelled.map(([, label]) => label), ...unlabelled].join(', ');
  },

  acceptAttribute() {
    return ChatAttachmentAcceptUtil.accepts.join(',');
  },

  isAccepted({ file }: { file: File }) {
    const extension = AttachmentBadgeUtil.extensionOf({
      fileName: file.name,
    }).toLowerCase();
    const type = file.type.toLowerCase();

    return ChatAttachmentAcceptUtil.accepts.some(accepted => {
      if (accepted.startsWith('.')) return accepted.slice(1) === extension;

      return accepted.endsWith('/*')
        ? type.startsWith(accepted.slice(0, -1))
        : accepted === type;
    });
  },

  partition({ files }: { files: FileList }) {
    const accepted: ChatAttachment[] = [];
    const rejected: string[] = [];

    for (let index = 0; index < files.length; index += 1) {
      const file = files.item(index);

      if (file) {
        if (ChatAttachmentAcceptUtil.isAccepted({ file })) {
          accepted.push({ file, id: crypto.randomUUID() });
        } else {
          rejected.push(file.name);
        }
      }
    }

    return { accepted, rejected };
  },
};
